import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PDF_TEMPLATES, type PdfTemplate, type ShopProfile, type TenantSettings } from "@autorepair/contracts";
import { tenantSettingsApi, shopProfileApi, withBase, ApiError } from "../lib/api";
import { parseMoneyInput } from "../lib/money";
import { COUNTRY_NAMES, SUPPORTED_COUNTRY_CODES, postalCodeLabel, regionCodesFor, regionFieldLabel } from "../lib/geo";
import { formatPhoneAsTyped } from "../lib/phone";
import { applyDigitPreservingTransform } from "../lib/inputCursor";
import { useConfirmGate } from "../lib/useConfirm";
import { useNotice } from "../lib/useNotice";
import { useShopType, useShopTypeLabels } from "../lib/theme";
import { Spinner } from "../components/ui-modern";
import {
  Eyebrow,
  ModernButton,
  ModernCard,
  ModernCheckboxRow,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernNoticeBanner,
  ModernSelect,
  ModernTextarea,
} from "../components/ui-modern";

const PDF_TEMPLATE_LABELS: Record<PdfTemplate, string> = { classic: "Classic", modern: "Modern" };
const PDF_TEMPLATE_DESCRIPTIONS: Record<PdfTemplate, string> = {
  classic: "The original layout — parts/labor side by side, matching the shop's own long-used sample invoice.",
  modern: "Matches this app's own Invoice screen — a single itemized table and the same color/type style you see here.",
};

/** Same fraction<->percent conversion as SettingsPageClassic.tsx — kept as
 * a private, byte-for-byte duplicate (not imported) since the classic file
 * doesn't export it and this round's rule is "never touch Classic's own
 * file" (see docs/FRONTEND_BACKLOG.md Round 59's Shop Settings entry). */
function rateToPercent(rate: string): string {
  return (Number(rate) * 100).toString();
}
function percentToRate(percent: string): string {
  const n = Number(percent);
  if (Number.isNaN(n)) return "0.00000";
  return (n / 100).toFixed(5);
}

/**
 * The modern theme's Shop Settings screen — see docs/FRONTEND_BACKLOG.md
 * Round 59 and `Bay2Bill Redesign.dc.html` lines ~747-816 ("isSettings"
 * block).
 *
 * This screen is unusually mutation-heavy (logo upload/remove, 2 separate
 * PATCH resources — ShopProfile and TenantSettings — several immediate-
 * apply toggles, one cross-field validation, and a must-keep-one-on
 * interdependency guard), so rather than reimplementing any of that with
 * subtly different logic, every mutation here is a straight duplicate of
 * SettingsPageClassic.tsx's own — same query keys (["shop-profile"],
 * ["tenant-settings"]), same request payload shapes (field-for-field, same
 * comments on what's deliberately excluded), same validation. The one
 * deliberate behavior change: the mockup shows a SINGLE "Save settings"
 * button (not Classic's two separate "Save Shop Identity"/"Save Settings"
 * buttons for its two separate cards) — so both save mutations are fired
 * together from one confirm gate/button here, each still hitting its own
 * endpoint with its own unchanged payload and its own success/error
 * notice. Nothing else about when/what gets saved differs from Classic.
 */
export function SettingsPageModern() {
  return (
    <div>
      <div className="mb-5">
        <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">Owner only</Eyebrow>
        <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Shop settings</h1>
      </div>
      <ShopSettingsBody />
    </div>
  );
}

function ShopSettingsBody() {
  const shopProfileQuery = useQuery({ queryKey: ["shop-profile"], queryFn: shopProfileApi.get });
  const tenantSettingsQuery = useQuery({ queryKey: ["tenant-settings"], queryFn: tenantSettingsApi.get });

  if (shopProfileQuery.isLoading || tenantSettingsQuery.isLoading) {
    return <Spinner label="Loading shop settings…" />;
  }
  if (shopProfileQuery.error || !shopProfileQuery.data || tenantSettingsQuery.error || !tenantSettingsQuery.data) {
    return <ModernErrorBanner message="Could not load the shop's settings." />;
  }

  // Keyed by both tenantIds (never change for this session) so a form
  // reset only ever happens on a genuinely different tenant, matching
  // SettingsPageClassic.tsx's own per-section `key={...tenantId}` — a
  // background refetch or an immediate-toggle mutation's cache update must
  // never blow away in-progress edits in the text fields below.
  return (
    <ShopSettingsForm
      key={`${shopProfileQuery.data.shopProfile.tenantId}-${tenantSettingsQuery.data.settings.tenantId}`}
      shopProfile={shopProfileQuery.data.shopProfile}
      tenantSettings={tenantSettingsQuery.data.settings}
    />
  );
}

function ShopSettingsForm({ shopProfile, tenantSettings }: { shopProfile: ShopProfile; tenantSettings: TenantSettings }) {
  const shopType = useShopType();
  const shopTypeLabels = useShopTypeLabels();
  const queryClient = useQueryClient();

  // ---- Logo (mirrors ShopLogoCard in SettingsPageClassic.tsx) ----
  const logoNotice = useNotice();
  const pdfConfirm = useConfirmGate();
  const headerConfirm = useConfirmGate();
  const nameHeaderConfirm = useConfirmGate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function clearSelection(): void {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  // Same rationale as SettingsPageClassic.tsx's own constant: the header
  // (the taller of the 2 places the logo appears) is the real resolution
  // floor.
  const RECOMMENDED_LOGO_HEIGHT_PX = 120;

  function downloadLogo(): void {
    if (!shopProfile.logoUrl) return;
    const a = document.createElement("a");
    a.href = withBase(shopProfile.logoUrl);
    a.download = "shop-logo";
    a.click();
  }

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Choose an image file first.");
      return shopProfileApi.uploadLogo(file);
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["shop-profile"], res);
      clearSelection();
      logoNotice.showSuccess("Logo uploaded.");
    },
    onError: (err) => logoNotice.showError(err instanceof ApiError || err instanceof Error ? err.message : "Could not upload the logo."),
  });

  const removeMutation = useMutation({
    mutationFn: () => shopProfileApi.removeLogo(),
    onSuccess: (res) => {
      queryClient.setQueryData(["shop-profile"], res);
      logoNotice.showSuccess("Logo removed.");
    },
    onError: (err) => logoNotice.showError(err instanceof ApiError ? err.message : "Could not remove the logo."),
  });

  const showOnDocumentsMutation = useMutation({
    mutationFn: (value: boolean) => shopProfileApi.update({ showLogoOnDocuments: value }),
    onSuccess: (res) => queryClient.setQueryData(["shop-profile"], res),
    onError: (err) => logoNotice.showError(err instanceof ApiError ? err.message : "Could not save this setting."),
  });

  const showInHeaderMutation = useMutation({
    mutationFn: (value: boolean) => shopProfileApi.update({ showLogoInHeader: value }),
    onSuccess: (res) => queryClient.setQueryData(["shop-profile"], res),
    onError: (err) => logoNotice.showError(err instanceof ApiError ? err.message : "Could not save this setting."),
  });

  const showNameInHeaderMutation = useMutation({
    mutationFn: (value: boolean) => shopProfileApi.update({ showShopNameInHeader: value }),
    onSuccess: (res) => queryClient.setQueryData(["shop-profile"], res),
    onError: (err) => logoNotice.showError(err instanceof ApiError ? err.message : "Could not save this setting."),
  });

  // ---- Shop identity text fields (mirrors ShopProfileForm) ----
  // Classic's PDF template never got the flexField1/no-vehicle treatment
  // Modern's did (see docs/SHOP_TYPES_PLAN.md) — the picker below hides
  // Classic entirely for a detailing tenant, so a pre-existing 'classic'
  // value is silently upgraded to 'modern' here too, otherwise hitting Save
  // without touching this section would resubmit the now-hidden 'classic'
  // value and hit the backend's own guard for it.
  const [identity, setIdentity] = useState<ShopProfile>(() =>
    shopType === "detailing" && shopProfile.pdfTemplate === "classic" ? { ...shopProfile, pdfTemplate: "modern" } : shopProfile,
  );
  const identityNotice = useNotice();
  function setIdentityField<K extends keyof ShopProfile>(key: K, value: ShopProfile[K]): void {
    setIdentity((f) => ({ ...f, [key]: value }));
  }
  // Which template's popup preview is open, if any — null closes it. A
  // modal with an <iframe>, not a new-tab link: a plain <a target="_blank">
  // here hit the dev proxy's SPA-fallback bypass (see vite.config.ts's own
  // doc comment on that failure mode) and, in production, would leave the
  // shop navigating away from Settings just to glance at a preview.
  const [previewTemplate, setPreviewTemplate] = useState<PdfTemplate | null>(null);

  const identitySaveMutation = useMutation({
    mutationFn: () =>
      shopProfileApi.update({
        displayName: identity.displayName,
        legalName: identity.legalName,
        // Non-null by contract now (CAN-SPAM physical-address requirement) —
        // safe to assert here since the form below marks these `required`,
        // so the browser blocks submit before this mutation ever runs.
        addressLine1: identity.addressLine1!,
        addressLine2: identity.addressLine2,
        city: identity.city!,
        region: identity.region!,
        postalCode: identity.postalCode!,
        country: identity.country,
        pdfTemplate: identity.pdfTemplate,
        phone: identity.phone,
        fax: identity.fax,
        email: identity.email,
        website: identity.website,
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(["shop-profile"], res);
      identityNotice.showSuccess("Saved.");
    },
    onError: (err) => identityNotice.showError(err instanceof ApiError ? err.message : "Could not save the shop's identity."),
  });

  // ---- Tax, shop supplies, invoice wording, vehicle image (mirrors
  // TaxAndTermsForm) ----
  const [settingsForm, setSettingsForm] = useState<TenantSettings>(tenantSettings);
  const [shopSuppliesCapText, setShopSuppliesCapText] = useState(tenantSettings.shopSuppliesCap ?? "");
  const settingsNotice = useNotice();

  const settingsSaveMutation = useMutation({
    mutationFn: (settings: TenantSettings) =>
      tenantSettingsApi.update({
        partsTaxRate: settings.partsTaxRate,
        laborTaxRate: settings.laborTaxRate,
        subletTaxRate: settings.subletTaxRate,
        partsTaxRateVisible: settings.partsTaxRateVisible,
        laborTaxRateVisible: settings.laborTaxRateVisible,
        subletTaxRateVisible: settings.subletTaxRateVisible,
        shopSuppliesRate: settings.shopSuppliesRate,
        shopSuppliesCap: settings.shopSuppliesCap,
        shopSuppliesTaxable: settings.shopSuppliesTaxable,
        shopSuppliesEnabled: settings.shopSuppliesEnabled,
        // defaultLaborRate deliberately not sent — same as
        // SettingsPageClassic.tsx (labor is a manual flat-amount entry, not
        // hours × rate, so this setting currently has no consumer).
        invoiceTermsText: settings.invoiceTermsText,
        customerNumberPrefix: settings.customerNumberPrefix,
        invoiceNumberPrefix: settings.invoiceNumberPrefix,
        estimateNumberPrefix: settings.estimateNumberPrefix,
        // vehicleImageEnabled deliberately not sent — its checkbox was
        // removed from this form (tmp/Notes.txt, 2026-09-15); omitting it
        // here leaves whatever value is already saved untouched.
        taxLabel: settings.taxLabel,
        taxRegistrationNumber: settings.taxRegistrationNumber,
        flexField1Label: settings.flexField1Label,
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(["tenant-settings"], res);
      settingsNotice.showSuccess("Saved.");
    },
    onError: (err) => settingsNotice.showError(err instanceof ApiError ? err.message : "Could not save these settings."),
  });

  // ---- The single combined "Save settings" action ----
  const saveConfirm = useConfirmGate();

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const shopSuppliesCapEntered = shopSuppliesCapText.trim() !== "";
    const shopSuppliesCap = shopSuppliesCapEntered ? parseMoneyInput(shopSuppliesCapText) : null;
    if (shopSuppliesCapEntered && shopSuppliesCap === null) {
      settingsNotice.showError('Amount should look like "25.00".');
      return;
    }
    saveConfirm.request(() => {
      identitySaveMutation.mutate();
      settingsSaveMutation.mutate({ ...settingsForm, shopSuppliesCap });
    });
  }

  const saving = identitySaveMutation.isPending || settingsSaveMutation.isPending;

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.65fr)_minmax(280px,22rem)]">
      <form id="shop-settings-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
        {/* ---- Shop identity ---- */}
        <ModernCard>
          <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Shop identity</h2>
          <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">Prints on every invoice and estimate.</p>
          <ModernNoticeBanner notice={logoNotice.notice} />

          <div className="mb-4 flex flex-wrap items-center gap-4 border-b border-[#f1f4f9] pb-4">
            <span className="flex h-[78px] w-[190px] flex-none items-center justify-center rounded-[10px] border border-[#e2e7ee] bg-white p-2">
              {previewUrl || shopProfile.logoUrl ? (
                <img src={previewUrl ?? withBase(shopProfile.logoUrl!)} alt="Shop logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-[11.5px] text-[#8b97a8]">No logo</span>
              )}
            </span>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="hidden"
              />
              <div className="flex flex-wrap gap-2">
                {!file && (
                  <ModernButton type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
                    {shopProfile.logoUrl ? "Replace" : "Choose File"}
                  </ModernButton>
                )}
                {file && (
                  <>
                    <span className="self-center text-[13px] text-[#4a5361]">{file.name}</span>
                    <ModernButton type="button" onClick={() => uploadMutation.mutate()} disabled={uploadMutation.isPending}>
                      {uploadMutation.isPending ? "Uploading…" : "Upload"}
                    </ModernButton>
                    <ModernButton type="button" variant="secondary" onClick={clearSelection} disabled={uploadMutation.isPending}>
                      Cancel
                    </ModernButton>
                  </>
                )}
                {!file && shopProfile.logoUrl && (
                  <>
                    <ModernButton type="button" variant="secondary" onClick={downloadLogo}>
                      Download
                    </ModernButton>
                    <ModernButton
                      type="button"
                      variant="secondary"
                      className="border-[#f3d7d5] text-[#b3261e] hover:border-[#f3d7d5] hover:text-[#b3261e]"
                      onClick={() => removeMutation.mutate()}
                      disabled={removeMutation.isPending}
                    >
                      Remove
                    </ModernButton>
                  </>
                )}
              </div>
              <span className="text-[12px] text-[#8b97a8]">At least {RECOMMENDED_LOGO_HEIGHT_PX}px tall, rectangular works best.</span>
            </div>
          </div>

          <ModernNoticeBanner notice={identityNotice.notice} />
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <ModernField label="Shop name" htmlFor="m-displayName" className="sm:col-span-2" required>
              <ModernInput
                id="m-displayName"
                required
                value={identity.displayName}
                onChange={(e) => setIdentityField("displayName", e.target.value)}
              />
            </ModernField>
            <ModernField label="Country" htmlFor="m-country">
              <ModernSelect
                id="m-country"
                value={identity.country}
                onChange={(e) => setIdentityField("country", e.target.value as ShopProfile["country"])}
              >
                {SUPPORTED_COUNTRY_CODES.map((code) => (
                  <option key={code} value={code}>
                    {COUNTRY_NAMES[code]}
                  </option>
                ))}
              </ModernSelect>
            </ModernField>
            <ModernField label="Address" htmlFor="m-addressLine1" required className="sm:col-span-2">
              <ModernInput
                id="m-addressLine1"
                className="mb-2"
                required
                value={identity.addressLine1 ?? ""}
                onChange={(e) => setIdentityField("addressLine1", e.target.value || null)}
              />
              <ModernInput
                id="m-addressLine2"
                placeholder="Apt, suite, etc. (optional)"
                value={identity.addressLine2 ?? ""}
                onChange={(e) => setIdentityField("addressLine2", e.target.value || null)}
              />
            </ModernField>
            <div className="grid grid-cols-3 gap-2 sm:col-span-2">
              <ModernInput
                aria-label="City"
                required
                value={identity.city ?? ""}
                onChange={(e) => setIdentityField("city", e.target.value || null)}
              />
              <ModernSelect
                aria-label={regionFieldLabel(identity.country)}
                required
                value={identity.region ?? ""}
                onChange={(e) => setIdentityField("region", e.target.value || null)}
              >
                <option value="">{regionFieldLabel(identity.country)}</option>
                {regionCodesFor(identity.country).map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </ModernSelect>
              <ModernInput
                aria-label={postalCodeLabel(identity.country)}
                required
                value={identity.postalCode ?? ""}
                onChange={(e) => setIdentityField("postalCode", e.target.value || null)}
              />
            </div>
            <ModernField label="Phone" htmlFor="m-phone">
              <ModernInput
                id="m-phone"
                value={identity.phone ?? ""}
                onChange={(e) => setIdentityField("phone", applyDigitPreservingTransform(e.target, formatPhoneAsTyped) || null)}
              />
            </ModernField>
            <ModernField label="Fax" htmlFor="m-fax">
              <ModernInput
                id="m-fax"
                value={identity.fax ?? ""}
                onChange={(e) => setIdentityField("fax", applyDigitPreservingTransform(e.target, formatPhoneAsTyped) || null)}
              />
            </ModernField>
            <ModernField label="Shop email" htmlFor="m-shopEmail" className="sm:col-span-2">
              <ModernInput
                id="m-shopEmail"
                type="email"
                value={identity.email ?? ""}
                onChange={(e) => setIdentityField("email", e.target.value || null)}
              />
              <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">
                To email estimates and invoices to your customers directly from Bay2Bill, enter a valid email address that you
                actively monitor. Customers' replies are sent to this address.
              </p>
            </ModernField>
            <ModernField label="Website" htmlFor="m-shopWebsite" className="sm:col-span-2">
              <ModernInput
                id="m-shopWebsite"
                value={identity.website ?? ""}
                onChange={(e) => setIdentityField("website", e.target.value || null)}
              />
            </ModernField>
          </div>
        </ModernCard>

        {/* ---- Invoice template ---- */}
        <ModernCard>
          <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Invoice template</h2>
          <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">
            Applies to every invoice and estimate PDF going forward — an already-issued invoice keeps rendering however
            it always has. Preview shows a sample invoice with your own shop name and logo, so you can see your branding
            in each template.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {PDF_TEMPLATES.filter((template) => template !== "classic" || shopType !== "detailing").map((template) => (
              <label
                key={template}
                className={`flex cursor-pointer flex-col gap-1.5 rounded-[10px] border px-3.5 py-3 ${
                  identity.pdfTemplate === template ? "border-[#0f6d86] bg-[#eff7f9]" : "border-[#e2e7ee]"
                }`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pdfTemplate"
                    checked={identity.pdfTemplate === template}
                    onChange={() => setIdentityField("pdfTemplate", template)}
                  />
                  <span className="text-[13.5px] font-semibold text-[#071a33]">{PDF_TEMPLATE_LABELS[template]}</span>
                </span>
                <span className="text-[12px] text-[#6b7a8f]">{PDF_TEMPLATE_DESCRIPTIONS[template]}</span>
                <button
                  type="button"
                  className="self-start text-[12.5px] font-semibold text-[#0f6d86] underline-offset-2 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPreviewTemplate(template);
                  }}
                >
                  Preview →
                </button>
              </label>
            ))}
          </div>
        </ModernCard>

        {previewTemplate && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={`${PDF_TEMPLATE_LABELS[previewTemplate]} template preview`}
          >
            <div
              className="flex h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white p-4"
              style={{ boxShadow: "0 20px 50px rgba(0,0,0,.35)" }}
            >
              <div className="mb-3 flex items-start justify-between gap-4">
                <h2 className="text-[17px] font-bold text-[#071a33]">{PDF_TEMPLATE_LABELS[previewTemplate]} template preview</h2>
                <ModernButton type="button" variant="secondary" onClick={() => setPreviewTemplate(null)}>
                  Close
                </ModernButton>
              </div>
              <iframe
                title={`${PDF_TEMPLATE_LABELS[previewTemplate]} template preview`}
                src={shopProfileApi.pdfPreviewUrl(previewTemplate, settingsForm.taxLabel, settingsForm.taxRegistrationNumber)}
                className="min-h-0 flex-1 rounded-[10px] border border-[#e2e7ee]"
              />
            </div>
          </div>
        )}

        {/* ---- Rates & tax ---- */}
        <ModernCard>
          <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Rates &amp; tax</h2>
          <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">
            A nonzero rate means that category is charged tax by default on new lines — set it to 0% for a category
            that's never taxed. Applied to new work orders; issued invoices keep the rate they were issued with. "Show
            on PDF" prints that category's rate as its own small line under Tax on the estimate/invoice. Any
            individual line can be marked "Tax exempt" to override this default either way, from its own Add/Edit
            form.
          </p>
          <ModernNoticeBanner notice={settingsNotice.notice} />
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
            <ModernField label={`${shopTypeLabels.partLabel} tax %`} htmlFor="m-partsTax" required>
              <ModernInput
                id="m-partsTax"
                required
                inputMode="decimal"
                value={rateToPercent(settingsForm.partsTaxRate)}
                onChange={(e) => setSettingsForm((f) => ({ ...f, partsTaxRate: percentToRate(e.target.value) }))}
              />
              <ModernCheckboxRow
                className="mt-2 text-[12.5px]"
                checked={settingsForm.partsTaxRateVisible}
                onChange={(next) => setSettingsForm((f) => ({ ...f, partsTaxRateVisible: next }))}
              >
                Show on PDF
              </ModernCheckboxRow>
            </ModernField>
            <ModernField label={`${shopTypeLabels.laborLabel} tax %`} htmlFor="m-laborTax">
              <ModernInput
                id="m-laborTax"
                inputMode="decimal"
                value={rateToPercent(settingsForm.laborTaxRate)}
                onChange={(e) => setSettingsForm((f) => ({ ...f, laborTaxRate: percentToRate(e.target.value) }))}
              />
              <ModernCheckboxRow
                className="mt-2 text-[12.5px]"
                checked={settingsForm.laborTaxRateVisible}
                onChange={(next) => setSettingsForm((f) => ({ ...f, laborTaxRateVisible: next }))}
              >
                Show on PDF
              </ModernCheckboxRow>
            </ModernField>
            <ModernField label={`${shopTypeLabels.subletLabel} tax %`} htmlFor="m-subletTax">
              <ModernInput
                id="m-subletTax"
                inputMode="decimal"
                value={rateToPercent(settingsForm.subletTaxRate)}
                onChange={(e) => setSettingsForm((f) => ({ ...f, subletTaxRate: percentToRate(e.target.value) }))}
              />
              <ModernCheckboxRow
                className="mt-2 text-[12.5px]"
                checked={settingsForm.subletTaxRateVisible}
                onChange={(next) => setSettingsForm((f) => ({ ...f, subletTaxRateVisible: next }))}
              >
                Show on PDF
              </ModernCheckboxRow>
            </ModernField>
            {identity.country === "CA" && (
              <>
                <ModernField label="Tax label" htmlFor="m-taxLabel" required>
                  <ModernInput
                    id="m-taxLabel"
                    required
                    maxLength={40}
                    value={settingsForm.taxLabel}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, taxLabel: e.target.value }))}
                  />
                  <p className="mt-1.5 text-[11.5px] leading-relaxed text-[#8b97a8]">
                    Replaces the word &quot;Tax&quot; on work orders, invoices, and PDFs — HST only applies in 5 of 10
                    provinces, so this is free text rather than a fixed toggle (e.g. &quot;HST&quot;, &quot;GST/PST&quot;,
                    &quot;GST/QST&quot;).
                  </p>
                </ModernField>
                <ModernField label="GST/HST registration number" htmlFor="m-taxRegNumber">
                  <ModernInput
                    id="m-taxRegNumber"
                    value={settingsForm.taxRegistrationNumber ?? ""}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, taxRegistrationNumber: e.target.value || null }))}
                  />
                  <p className="mt-1.5 text-[11.5px] leading-relaxed text-[#8b97a8]">
                    Prints on the invoice and PDFs when set. Leave blank to hide it.
                  </p>
                </ModernField>
              </>
            )}
            {/* Its own full-width row, separated from the tax fields above
                (which can be 3 or 5 fields depending on country) — always
                starts fresh regardless of how those fill out, rather than
                falling wherever grid auto-placement happens to land it. */}
            <div className="sm:col-span-3 mt-1 border-t border-[#eef1f6] pt-3.5">
              <ModernCheckboxRow
                checked={settingsForm.shopSuppliesEnabled}
                onChange={(next) => setSettingsForm((f) => ({ ...f, shopSuppliesEnabled: next }))}
              >
                Enable Shop Supplies
              </ModernCheckboxRow>
              {!settingsForm.shopSuppliesEnabled && (
                <p className="m-0 mt-1.5 text-[12px] text-[#8b97a8]">
                  Off — no Shop Supplies charge on new or existing work orders, and it won&apos;t appear on
                  estimates or invoices.
                </p>
              )}
              {settingsForm.shopSuppliesEnabled && (
                <div className="mt-3.5 grid grid-cols-1 gap-3.5 sm:grid-cols-3">
                  <ModernField label={`Shop supplies % of ${shopTypeLabels.partLabel.toLowerCase()}s`} htmlFor="m-suppliesRate">
                    <ModernInput
                      id="m-suppliesRate"
                      inputMode="decimal"
                      value={rateToPercent(settingsForm.shopSuppliesRate)}
                      onChange={(e) => setSettingsForm((f) => ({ ...f, shopSuppliesRate: percentToRate(e.target.value) }))}
                    />
                  </ModernField>
                  <ModernField
                    label="Maximum shop supplies charge ($)"
                    htmlFor="m-suppliesCap"
                    className="sm:col-span-2"
                  >
                    <ModernInput
                      id="m-suppliesCap"
                      inputMode="decimal"
                      value={shopSuppliesCapText}
                      onChange={(e) => setShopSuppliesCapText(e.target.value)}
                    />
                    <span className="mt-1 block text-[12px] text-[#8b97a8]">
                      A dollar amount, not a percentage — caps the calculated charge above. Leave blank for no cap.
                    </span>
                  </ModernField>
                </div>
              )}
            </div>
            <div className="sm:col-span-3 mt-1 border-t border-[#eef1f6] pt-3.5">
              <ModernField label="Custom field label" htmlFor="m-flexField1Label">
                <ModernInput
                  id="m-flexField1Label"
                  value={settingsForm.flexField1Label ?? ""}
                  onChange={(e) => setSettingsForm((f) => ({ ...f, flexField1Label: e.target.value || null }))}
                />
                <p className="mt-1.5 text-[11.5px] leading-relaxed text-[#8b97a8]">
                  Adds one extra field to every work order, using whatever label you type here — e.g. a detailing
                  shop might use &quot;License Plate&quot;. Prints on estimates and invoices when set. Leave blank
                  to hide it everywhere.
                </p>
              </ModernField>
            </div>
          </div>
        </ModernCard>

        {/* ---- Invoice wording ---- */}
        <ModernCard>
          <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Invoice wording</h2>
          <p className="m-0 mb-3.5 text-[12.5px] text-[#6b7a8f]">Printed at the bottom of every invoice.</p>
          <ModernTextarea
            id="m-terms"
            rows={4}
            value={settingsForm.invoiceTermsText}
            onChange={(e) => setSettingsForm((f) => ({ ...f, invoiceTermsText: e.target.value }))}
          />
        </ModernCard>
      </form>

      <aside className="flex min-w-0 flex-col gap-3.5 lg:sticky lg:top-4">
        <ModernCard>
          <h2 className="m-0 mb-3 text-[14px] font-bold text-[#071a33]">Where the logo &amp; name show</h2>
          <div className="flex flex-col gap-2.5">
            {shopProfile.logoUrl && (
              <ModernCheckboxRow
                checked={shopProfile.showLogoInHeader}
                disabled={showInHeaderMutation.isPending || (shopProfile.showLogoInHeader && !shopProfile.showShopNameInHeader)}
                onChange={(next) => headerConfirm.request(() => showInHeaderMutation.mutate(next))}
                hint={
                  shopProfile.showLogoInHeader && !shopProfile.showShopNameInHeader
                    ? "Can't turn off with the shop name also hidden from the header"
                    : undefined
                }
              >
                In the app header, next to the shop name
              </ModernCheckboxRow>
            )}
            {shopProfile.logoUrl && (
              <ModernCheckboxRow
                checked={shopProfile.showShopNameInHeader}
                disabled={showNameInHeaderMutation.isPending || (shopProfile.showShopNameInHeader && !shopProfile.showLogoInHeader)}
                onChange={(next) => nameHeaderConfirm.request(() => showNameInHeaderMutation.mutate(next))}
                hint={
                  shopProfile.showShopNameInHeader && !shopProfile.showLogoInHeader
                    ? "Can't turn off with the logo also hidden from the header"
                    : undefined
                }
              >
                Shop name in the app header (independent of the logo)
              </ModernCheckboxRow>
            )}
            <ModernCheckboxRow
              checked={shopProfile.showLogoOnDocuments}
              disabled={!shopProfile.logoUrl || showOnDocumentsMutation.isPending}
              onChange={(next) => pdfConfirm.request(() => showOnDocumentsMutation.mutate(next))}
            >
              On invoice and estimate PDFs
            </ModernCheckboxRow>
          </div>
        </ModernCard>

        <ModernCard>
          <h2 className="m-0 mb-2.5 text-[14px] font-bold text-[#071a33]">Preview</h2>
          <div className="rounded-[10px] border border-[#e8ecf3] bg-[#fbfcfe] p-[13px]">
            <div className="text-[13px] font-bold text-[#071a33]">{identity.displayName || "Your Shop Name"}</div>
            <div className="mt-[3px] text-[11.5px] leading-relaxed text-[#6b7a8f]">
              {identity.addressLine1}
              {identity.addressLine2 ? `, ${identity.addressLine2}` : ""}
              {(identity.addressLine1 || identity.addressLine2) && <br />}
              {[identity.city, identity.region].filter(Boolean).join(", ")}
              {identity.postalCode ? ` ${identity.postalCode}` : ""}
              {identity.phone ? ` · ${identity.phone}` : ""}
            </div>
            {settingsForm.invoiceTermsText && (
              <div className="mt-2.5 border-t border-[#eef1f6] pt-2 text-[11px] text-[#8b97a8]">{settingsForm.invoiceTermsText}</div>
            )}
          </div>
        </ModernCard>

        <ModernButton type="submit" form="shop-settings-form" className="w-full" disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </ModernButton>
      </aside>

      <ModernConfirmDialog {...saveConfirm.dialogProps} message="Save these shop settings?" />
      <ModernConfirmDialog
        {...pdfConfirm.dialogProps}
        message={
          shopProfile.showLogoOnDocuments
            ? "Stop printing the logo on invoice/estimate PDFs? This only applies to documents issued from now on."
            : "Start printing the logo on invoice/estimate PDFs? This only applies to documents issued from now on."
        }
      />
      <ModernConfirmDialog
        {...headerConfirm.dialogProps}
        message={
          shopProfile.showLogoInHeader
            ? "Stop showing the logo in the website header? The shop name will still show on its own."
            : "Show the logo next to the shop name in the website header?"
        }
      />
      <ModernConfirmDialog
        {...nameHeaderConfirm.dialogProps}
        message={
          shopProfile.showShopNameInHeader
            ? "Stop showing the shop name in the website header? The logo will still show on its own."
            : "Show the shop name in the website header?"
        }
      />
    </div>
  );
}
